/**
 * RoofDetailsDropdownsAdmin — single-page editor for every dropdown
 * list used by the Roof Details sub-form (Builder + Components tab
 * "Roof Details Adjustments").
 *
 * Backed by `dropdown_options` (collection) via the existing
 * `/api/dropdowns/{category}` endpoints. Each category supports
 * add / remove / rename. Numeric Roof Details fields (Roof m², Roof
 * Angle, Stories) don't have dropdowns and aren't listed here.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Check, Loader2, RefreshCw, Layers } from 'lucide-react';

const CATEGORIES = [
  { key: 'roof_descriptions', label: 'Description' },
  { key: 'roof_types',        label: 'Roof Type' },
  { key: 'roof_brands',       label: 'Brand' },
  { key: 'roof_profiles',     label: 'Profile' },
  // Iter146 — Material Type is the 4th cascade tier for Iron roofs.
  // The list is intentionally the Iron-materials palette (Zinc / Galv
  // / Zcore / Maxx etc.) — the Components-tab cascade matches each
  // value against the rate-row substring to pre-select Iron sections.
  { key: 'material_types',    label: 'Material Type (Iron)' },
  { key: 'roof_pitches',      label: 'Pitch' },
  { key: 'roof_colours',      label: 'Colour' },
  { key: 'colour_conditions', label: 'Surface (Colour condition)' },
  { key: 'organic_growths',   label: 'Growth (Organic growth)' },
  { key: 'roof_ages',         label: 'Age' },
  { key: 'roof_states',       label: 'State' },
];

export default function RoofDetailsDropdownsAdmin() {
  const { api } = useAuth();
  const [active, setActive] = useState(CATEGORIES[0].key);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState('');
  const [editingIdx, setEditingIdx] = useState(-1);
  const [editValue, setEditValue] = useState('');

  const activeMeta = CATEGORIES.find(c => c.key === active) || CATEGORIES[0];

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dropdowns/${active}`);
      setOptions(Array.isArray(data) ? data : (data?.options || []));
      setEditingIdx(-1);
    } catch (err) {
      toast.error(`Load failed: ${err?.response?.data?.detail || err.message}`);
      setOptions([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active]);

  const addOption = async () => {
    const v = adding.trim();
    if (!v) return;
    if (options.includes(v)) { toast.error(`"${v}" already exists in ${activeMeta.label}`); return; }
    try {
      const { data } = await api.post(`/dropdowns/${active}/add`, { value: v });
      setOptions(Array.isArray(data) ? data : (data?.options || []));
      setAdding('');
      toast.success(`Added "${v}"`);
    } catch (err) {
      toast.error(`Add failed: ${err?.response?.data?.detail || err.message}`);
    }
  };

  const removeOption = async (v) => {
    if (!window.confirm(`Delete "${v}" from ${activeMeta.label}?\n\nNote: existing records keep their value, but it won't appear in dropdowns anymore.`)) return;
    try {
      const { data } = await api.post(`/dropdowns/${active}/remove`, { value: v });
      setOptions(Array.isArray(data) ? data : (data?.options || []));
      toast.success(`Removed "${v}"`);
    } catch (err) {
      toast.error(`Remove failed: ${err?.response?.data?.detail || err.message}`);
    }
  };

  const startEdit = (idx) => {
    setEditingIdx(idx);
    setEditValue(options[idx] || '');
  };

  const saveRename = async (idx) => {
    const oldV = options[idx];
    const newV = editValue.trim();
    if (!newV || newV === oldV) { setEditingIdx(-1); return; }
    if (options.includes(newV)) { toast.error(`"${newV}" already exists`); return; }
    try {
      const { data } = await api.post(`/dropdowns/${active}/rename`, {
        old_value: oldV, new_value: newV,
      });
      setOptions(Array.isArray(data) ? data : (data?.options || []));
      setEditingIdx(-1);
      toast.success(`Renamed "${oldV}" → "${newV}"`);
    } catch (err) {
      toast.error(`Rename failed: ${err?.response?.data?.detail || err.message}`);
    }
  };

  return (
    <div className="px-6 py-6 max-w-[1400px] mx-auto space-y-5" data-testid="roof-dropdowns-admin">
      <div className="flex items-start justify-between gap-4 flex-wrap border-b border-slate-200 pb-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Layers size={28} className="text-orange-600" />
            Roof Details Dropdowns
          </h1>
          <p className="text-sm text-slate-500 mt-1.5">
            Manage every dropdown list used by the Roof Details sub-form (Builder &amp; Components &gt; Roof Details Adjustments). Add, rename or remove options per category.
          </p>
        </div>
        <button
          data-testid="reload-btn"
          onClick={load}
          className="flex items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-sm font-medium"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Reload
        </button>
      </div>

      <div className="grid grid-cols-12 gap-5">
        {/* Category sidebar */}
        <aside className="col-span-12 md:col-span-3">
          <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
            <div className="px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-500 border-b border-slate-100 bg-slate-50">
              Categories
            </div>
            {CATEGORIES.map(c => (
              <button
                key={c.key}
                data-testid={`cat-tab-${c.key}`}
                onClick={() => setActive(c.key)}
                className={`w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-0 transition-colors ${
                  active === c.key
                    ? 'bg-orange-50 text-orange-700 font-semibold border-l-4 border-l-orange-500'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                {c.label}
                <span className="block text-[10px] text-slate-400 font-mono mt-0.5">{c.key}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* Editor */}
        <main className="col-span-12 md:col-span-9">
          <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
              <div>
                <h2 className="text-base font-bold text-slate-900">{activeMeta.label}</h2>
                <div className="text-[11px] text-slate-500">{options.length} option{options.length === 1 ? '' : 's'}</div>
              </div>
            </div>

            {/* Add new */}
            <div className="px-4 py-3 border-b border-slate-100 bg-orange-50/30">
              <div className="flex items-center gap-2">
                <input
                  data-testid="add-option-input"
                  value={adding}
                  onChange={(e) => setAdding(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addOption(); }}
                  placeholder={`Add new ${activeMeta.label.toLowerCase()}…`}
                  className="flex-1 text-sm border border-slate-300 rounded px-2 py-1.5"
                />
                <button
                  data-testid="add-option-btn"
                  onClick={addOption}
                  disabled={!adding.trim()}
                  className="flex items-center gap-1 px-3 py-1.5 rounded bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 disabled:opacity-40"
                >
                  <Plus size={14} /> Add
                </button>
              </div>
            </div>

            {/* Options list */}
            {loading ? (
              <div className="px-4 py-12 text-center text-sm text-slate-400">
                <Loader2 className="animate-spin inline-block mr-2" size={14} /> Loading…
              </div>
            ) : options.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-slate-400">
                No options yet — add the first one above.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {options.map((v, idx) => (
                  <li key={`${v}-${idx}`} className="flex items-center justify-between gap-3 px-4 py-2 hover:bg-slate-50" data-testid={`option-row-${v}`}>
                    {editingIdx === idx ? (
                      <>
                        <input
                          data-testid="edit-value-input"
                          autoFocus
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveRename(idx);
                            if (e.key === 'Escape') setEditingIdx(-1);
                          }}
                          className="flex-1 text-sm border border-orange-300 rounded px-2 py-1"
                        />
                        <button
                          data-testid="save-rename-btn"
                          onClick={() => saveRename(idx)}
                          className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-600 text-white text-xs hover:bg-emerald-700"
                        >
                          <Check size={12} /> Save
                        </button>
                        <button
                          data-testid="cancel-rename-btn"
                          onClick={() => setEditingIdx(-1)}
                          className="flex items-center gap-1 px-2 py-1 rounded border border-slate-300 text-slate-700 text-xs hover:bg-slate-100"
                        >
                          <X size={12} /> Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm text-slate-900">{v}</span>
                        <button
                          data-testid={`edit-option-${v}`}
                          onClick={() => startEdit(idx)}
                          className="flex items-center gap-1 px-2 py-1 rounded border border-slate-200 text-slate-600 text-xs hover:bg-slate-100"
                        >
                          <Pencil size={11} /> Edit
                        </button>
                        <button
                          data-testid={`remove-option-${v}`}
                          onClick={() => removeOption(v)}
                          className="flex items-center gap-1 px-2 py-1 rounded border border-rose-200 text-rose-600 text-xs hover:bg-rose-50"
                        >
                          <Trash2 size={11} /> Remove
                        </button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
